import { css } from '@emotion/react';
import { useCallback } from 'react';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { ConstraintSlider } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintSlider.component';

const constraintsNameStyle = (enabled: boolean) => (theme: Theme) => css({
  color: enabled ? theme.textColors.primary : theme.textColors.disabled,
  fontSize: 14,
  marginLeft: 10,
  overflow: 'hidden',
  paddingRight: 10,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const headingStyle = css({
  alignItems: 'center',
  display: 'flex',
});

const errorMessageStyle = (theme: Theme) => css({
  color: theme.textColors.danger,
  fontSize: 12,
  marginTop: 4,
});

type TerritoryOptimizationConstraintProps = {
  animationType?: 'success' | 'error';
  checked?: boolean;
  highestMin?: number | null;
  ids: { id: string; groupId?: string };
  label: string;
  lowestMax?: number | null;
  max?: number;
  maxValue?: number;
  min?: number;
  minValue?: number;

  onChange: ({ change, groupId, id }: {
    change: { isConstraint?: boolean; minConstraint?: number; maxConstraint?: number};
    groupId?: string;
    id: string;
  }) => void;
  onRemove?: (id: string) => void;
};

export const TerritoryOptimizationConstraint = ({
  animationType,
  checked = false,
  highestMin,
  ids: { id, groupId },
  label,
  lowestMax,
  max,
  maxValue,
  min,
  minValue,
  onChange,
}: TerritoryOptimizationConstraintProps) => {

  const [t] = useTranslation();
  const initialized = notNullsy(min) && notNullsy(max);
  const hasRange = initialized && min !== max;

  const handleCheckboxChange = useCallback(() => {
    onChange({ id, groupId, change: { isConstraint: !checked } });
  }, [checked, groupId, id, onChange]);

  const handleSliderChange = useCallback((minConstraint: number, maxConstraint: number) => {
    onChange({ id, groupId, change: { minConstraint, maxConstraint } });
  }, [groupId, id, onChange]);

  return (
    <div key={id} >
      <div css={headingStyle}>
        <CheckboxComponent
          checkedSetter={handleCheckboxChange}
          isChecked={checked ?? false}
          isDisabled={!hasRange}
        />

        <span css={constraintsNameStyle(hasRange)}>{label}</span>
      </div>

      {initialized && !hasRange && (
        <div css={errorMessageStyle}>
          {t('boundaryAI.Constraints.Error.hasNoRange')}
        </div>

      )}

      {hasRange && checked && (
        <ConstraintSlider
          animationType={animationType}
          highestMin={highestMin}
          lowestMax={lowestMax}
          max={max}
          min={min}
          maxValue={maxValue}
          minValue={minValue}
          onChange={handleSliderChange}
        />
      )}
    </div>
  );
};
